/**
 * GroupDetails — group header, membership CTA and the Posts / User Approval /
 * Members / About tabs. Posts reuse GroupContext so likes, saves and comments
 * stay in sync with the Home Groups feed (mobile groupDetails parity: the
 * approval and member tabs are admin-only).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { GROUP_PRIVACY, GROUP_STATUS } from '@wlb/shared/constants';
import { mapGroupDetailsToGroup } from '@wlb/shared/api/groups/groupsMapper';
import { buildShareGroupUrl } from '@wlb/shared/config/env';
import groupService from '@wlb/shared/api/groups/groupsService';
import { GROUP_ROLE_ID } from '@wlb/shared/api/groups/groupsTypes';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { getGroupJoinActionLabelKey } from '@wlb/shared/utils/groupJoinAction';
import type { ApiError } from '@wlb/shared/types/api';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { ChevronLeftIcon, GlobeIcon, GroupsTabIcon, LockIcon, PostMenuIcon, PencilIcon, ShareIcon, LinkIcon, LogoutIcon, TrashIcon } from '../components/ui/icons';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { ListStatus } from '../components/ui/ListStatus';
import { isGroupAdmin } from '../features/groups/groupPermissions';
import { GroupPostsFeed } from '../features/groups/GroupPostsFeed';
import { GroupDetailsSkeleton, GroupRowsSkeleton } from '../features/groups/GroupSkeletons';
import { GroupImage } from '../features/groups/GroupImage';
import { UserApprovalCard } from '../features/groups/UserApprovalCard';
import { resolveGroupImageUrl } from '../features/groups/groupMedia';
import {
  readGroupDetailTab,
  writeGroupDetailTab,
} from '../features/groups/groupsViewState';
import { useGroupDetails } from '../features/groups/useGroupDetails';
import { useGroupJoinRequests } from '../features/groups/useGroupJoinRequests';
import { useScrollRestore } from '../features/groups/useScrollRestore';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import { anchoredMenuPosition } from '../utils/anchoredMenuPosition';
import '../features/feed/feed.css';
import '../features/groups/groups.css';

type DetailTab = 'posts' | 'userApproval' | 'members' | 'about';

const TAB_LABEL_KEYS: Record<DetailTab, string> = {
  posts: 'groupDetails.tabs.posts',
  userApproval: 'groupDetails.tabs.userApproval',
  members: 'groupDetails.tabs.members',
  about: 'groupDetails.tabs.about',
};

/* Mirrors `.group-details__menu` in groups.css (max-width, and its tallest
   admin variant), which only decides which side of the kebab it opens on. */
const GROUP_MENU_WIDTH = 240;
const GROUP_MENU_HEIGHT = 300;
const GROUP_MENU_VIEWPORT_EDGE = 14;

export function GroupDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();
  const openUserProfile = useOpenUserProfile();
  const {
    groupPosts,
    groupPostsLoading,
    groupPostsError,
    groupPostsLoaded,
    fetchGroupPosts,
    joinGroup,
    leaveGroup,
    deleteGroup,
  } = useGroup();
  const { detail, loading, error, reload } = useGroupDetails(groupId);
  const [activeTab, setActiveTab] = useState<DetailTab>('posts');
  const [membershipBusy, setMembershipBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [assignAdminOpen, setAssignAdminOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLButtonElement | null>(null);
  const restoredTab = useRef(false);

  const group = detail?.data;
  const isAdmin = isGroupAdmin(detail);

  const tabs = useMemo<DetailTab[]>(
    () => (isAdmin ? ['posts', 'userApproval', 'members', 'about'] : ['posts', 'about']),
    [isAdmin],
  );

  const loadPosts = useCallback(() => {
    if (!groupId) return;
    fetchGroupPosts(groupId).catch(() => undefined);
  }, [groupId, fetchGroupPosts]);

  useEffect(loadPosts, [loadPosts]);

  /** Member-initiated reload of this group's posts, past the scope cache. */
  const refreshPosts = useCallback(() => {
    if (!groupId) return;
    fetchGroupPosts(groupId, { force: true }).catch(() => undefined);
  }, [groupId, fetchGroupPosts]);

  // GroupContext holds a single shared post list, so scope it to this group;
  // otherwise the previous screen's posts flash here until the fetch resolves.
  const posts = useMemo(
    () => groupPosts.filter(post => String(post.groupId) === String(groupId)),
    [groupPosts, groupId],
  );

  // Restore the tab the member was on before they opened a profile, once the
  // detail response tells us which tabs they are allowed to see.
  useEffect(() => {
    if (restoredTab.current || !detail) return;
    restoredTab.current = true;
    const stored = readGroupDetailTab(groupId, tabs);
    if (stored) setActiveTab(stored as DetailTab);
  }, [detail, groupId, tabs]);

  useEffect(() => {
    if (!detail) return;
    if (!tabs.includes(activeTab)) setActiveTab('posts');
  }, [detail, tabs, activeTab]);

  const refreshDetail = useCallback(() => reload(), [reload]);
  const joinRequests = useGroupJoinRequests(
    groupId,
    isAdmin && activeTab === 'userApproval',
    refreshDetail,
  );

  // The detail payload carries the pending count for every tab; once the
  // approval tab has loaded, its live count wins so the badge tracks reviews.
  const pendingRequestCount =
    activeTab === 'userApproval' && !joinRequests.loading && !joinRequests.error
      ? joinRequests.totalCount
      : (group?.activity.joinRequestCount ?? 0);

  const tabReady =
    activeTab === 'posts'
      ? !groupPostsLoading || posts.length > 0
      : activeTab === 'userApproval'
        ? !joinRequests.loading
        : Boolean(detail);
  useScrollRestore(`group:${groupId ?? 'unknown'}:${activeTab}`, tabReady);

  const selectTab = (tab: DetailTab) => {
    setActiveTab(tab);
    writeGroupDetailTab(groupId, tab);
  };

  const runMembershipAction = useCallback((action: () => Promise<void>) => {
    setMembershipBusy(true);
    action()
      .then(reload)
      .catch((err: unknown) => {
        ToastService.show({
          type: 'error',
          message: getApiErrorMessage(err as ApiError) || t('common.errors.generic'),
        });
      })
      .finally(() => setMembershipBusy(false));
  }, [reload, t]);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchorRect(null);
    menuAnchorRef.current = null;
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen, closeMenu]);

  const handleMenuAction = useCallback((key: string) => {
    closeMenu();
    if (!group) return;
    switch (key) {
      case 'editGroup':
        navigate(`/groups/${group.id}/edit`);
        break;
      case 'shareGroup':
        if (navigator.share) {
          navigator.share({
            title: group.groupName,
            text: t('groupDetails.shareMessage', { name: group.groupName }),
            url: buildShareGroupUrl(group.id),
          }).catch(() => undefined);
        } else {
          navigator.clipboard?.writeText(buildShareGroupUrl(group.id)).then(
            () => ToastService.show({ type: 'success', message: t('groupDetails.options.copyLink') }),
            () => undefined,
          );
        }
        break;
      case 'copyLink':
        navigator.clipboard?.writeText(buildShareGroupUrl(group.id)).then(
          () => ToastService.show({ type: 'success', message: t('groupDetails.options.copyLink') }),
          () => undefined,
        );
        break;
      case 'leaveGroup':
        // Admin can leave if there's at least one other admin in the group.
        if (isAdmin) {
          const adminCount = detail.members.filter(m => m.isAdmin || m.isOwner).length;
          if (adminCount > 1) {
            setLeaveConfirmOpen(true);
          } else {
            setAssignAdminOpen(true);
          }
        } else {
          setLeaveConfirmOpen(true);
        }
        break;
      case 'deleteGroup':
        setDeleteConfirmOpen(true);
        break;
      default:
        break;
    }
  }, [closeMenu, detail, group, isAdmin, navigate, t]);

  const handleConfirmLeave = useCallback(() => {
    if (!group) return;
    setLeaveConfirmOpen(false);
    runMembershipAction(() => leaveGroup(group.id));
  }, [group, leaveGroup, runMembershipAction]);

  const handleAssignAdmin = useCallback((memberId: string, memberName: string) => {
    if (!group) return;
    setMembershipBusy(true);
    groupService.changeMemberRole(String(group.id), Number(memberId), GROUP_ROLE_ID.Admin)
      .then(() => {
        ToastService.show({ type: 'success', message: t('groupDetails.options.makeHostTitle', { name: memberName }) });
        reload();
      })
      .catch((err: unknown) => {
        ToastService.show({
          type: 'error',
          message: getApiErrorMessage(err as ApiError) || t('common.errors.generic'),
        });
      })
      .finally(() => setMembershipBusy(false));
  }, [group, reload, t]);

  if (loading && !detail) {
    return <GroupDetailsSkeleton />;
  }

  if (error || !detail || !group) {
    return (
      <ListStatus
        variant="error"
        message={error || t('groupDetails.errors.loadFailed')}
        onAction={reload}
        busy={loading}
      />
    );
  }

  const coverUrl = resolveGroupImageUrl(group.groupCoverImage);
  const avatarUrl = resolveGroupImageUrl(group.groupProfileImage);
  const adminAvatarUrl = resolveGroupImageUrl(group.admin.avatar);
  const isPrivate = String(group.privacyType).toLowerCase() === GROUP_PRIVACY.PRIVATE.toLowerCase();
  const isOwner = group.isOwner === true || group.status === GROUP_STATUS.OWNER;
  const isPending = group.status === GROUP_STATUS.PENDING;
  const isMember = isOwner || group.status === GROUP_STATUS.JOINED;
  const canPost = isMember || isAdmin;
  const PrivacyIcon = isPrivate ? LockIcon : GlobeIcon;

  const openCreatePost = () =>
    navigate('/create', {
      state: {
        group: { id: String(group.id), name: group.groupName, image: avatarUrl ?? null },
      },
    });

  const renderMembershipAction = () => {
    if (isOwner) return null;
    if (isPending) {
      return <span className="group-row__status">{t('home.allGroups.pending')}</span>;
    }
    if (isMember) {
      return (
        <Button
          variant="outline"
          disabled={membershipBusy}
          onClick={() => runMembershipAction(() => leaveGroup(group.id))}
        >
          {t('groupDetails.options.leaveGroup')}
        </Button>
      );
    }
    return (
      <Button
        disabled={membershipBusy}
        onClick={() => runMembershipAction(() => joinGroup(group.id, mapGroupDetailsToGroup(group)))}
      >
        {t(getGroupJoinActionLabelKey(group, 'groupDetails.options'))}
      </Button>
    );
  };

  const renderUserApproval = () => (
    <section className="group-approval">
      <h2 className="group-approval__title">
        {t('groupDetails.userApproval.pendingRequests', { count: joinRequests.totalCount })}
      </h2>
      {joinRequests.loading ? (
        <GroupRowsSkeleton count={3} />
      ) : joinRequests.error ? (
        <ListStatus
          variant="error"
          message={joinRequests.error}
          onAction={joinRequests.reload}
          busy={joinRequests.loading}
        />
      ) : joinRequests.requests.length === 0 ? (
        <ListStatus
          variant="empty"
          title={t('groupDetails.userApproval.noPendingRequests')}
          onAction={joinRequests.reload}
          busy={joinRequests.loading}
        />
      ) : (
        <ul className="group-approval__list">
          {joinRequests.requests.map(request => (
            <UserApprovalCard
              key={request.id}
              request={request}
              loading={joinRequests.reviewingId === request.groupJoinRequestId}
              disabled={joinRequests.reviewingId != null}
              onApprove={joinRequests.approve}
              onDecline={joinRequests.decline}
              onOpenProfile={openUserProfile}
            />
          ))}
        </ul>
      )}
    </section>
  );

  const renderMembers = () => {
    if (detail.members.length === 0) {
      // Reached only after the detail request succeeded, so this is a true empty.
      return (
        <ListStatus
          variant="empty"
          title={t('groupDetails.activity.noMembers')}
          onAction={reload}
          busy={loading}
        />
      );
    }
    return (
      <ul className="group-members">
        {detail.members.map(member => (
          <li key={member.id} className="group-members__item">
            <button
              type="button"
              className="group-row__avatar-btn"
              onClick={() => openUserProfile(member.profileUserGuid)}
              aria-label={t('home.accessibility.openUserProfile', { username: member.name })}
            >
              <Avatar
                src={member.profileImage}
                name={member.name}
                username={member.username}
                seed={member.profileUserGuid ?? member.userId ?? member.id}
                size="lg"
              />
            </button>
            <button
              type="button"
              className="group-row__main"
              onClick={() => openUserProfile(member.profileUserGuid)}
            >
              <span className="group-row__meta">
                <span className="group-row__name">{member.name}</span>
                {member.isAdmin && (
                  <span className="group-members__badge">{t('groupDetails.admin.badge')}</span>
                )}
                {member.approvedBy && (
                  <span className="group-row__members">
                    {t('groupDetails.activity.approvedBy', { name: member.approvedBy })}
                  </span>
                )}
              </span>
            </button>
            {isAdmin && !member.isAdmin && (
              <button
                type="button"
                className="group-members__action"
                disabled={membershipBusy}
                onClick={() => handleAssignAdmin(String(member.userId ?? member.id), member.name)}
              >
                {t('groupDetails.options.makeHost')}
              </button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const renderAbout = () => (
    <div className="group-about">
      <p className="group-about__description">{group.groupDescription}</p>
      <section className="group-about__section">
        <h2 className="group-about__title">{t('groupDetails.admin.title')}</h2>
        <button
          type="button"
          className="group-row__main"
          onClick={() => openUserProfile(group.admin.profileUserGuid)}
        >
          <Avatar
            src={adminAvatarUrl}
            name={group.admin.name}
            seed={group.admin.profileUserGuid ?? group.admin.userId ?? group.admin.name}
            size="lg"
          />
          <span className="group-row__meta">
            <span className="group-row__name">{group.admin.name}</span>
            <span className="group-row__members">{t('groupDetails.admin.creatorRole')}</span>
          </span>
        </button>
      </section>
      <section className="group-about__section">
        <h2 className="group-about__title">{t('groupDetails.activity.title')}</h2>
        <dl className="group-about__stats">
          <div>
            <dt>{t('groupDetails.activity.postsToday')}</dt>
            <dd>{group.activity.postCount.toLocaleString()}</dd>
          </div>
          <div>
            <dt>{t('groupDetails.activity.postsThisMonth')}</dt>
            <dd>{group.activity.monthPostCount.toLocaleString()}</dd>
          </div>
          <div>
            <dt>{t('groupDetails.activity.totalMembers')}</dt>
            <dd>{group.activity.memberCount.toLocaleString()}</dd>
          </div>
          <div>
            <dt>{t('groupDetails.activity.newMembersThisWeek')}</dt>
            <dd>{group.activity.newMemberCount.toLocaleString()}</dd>
          </div>
        </dl>
      </section>
    </div>
  );

  const renderTabContent = () => {
    if (activeTab === 'userApproval') return renderUserApproval();
    if (activeTab === 'members') return renderMembers();
    if (activeTab === 'about') return renderAbout();
    return (
      <GroupPostsFeed
        posts={posts}
        loading={groupPostsLoading}
        error={groupPostsError}
        loaded={groupPostsLoaded}
        onRetry={refreshPosts}
        emptyMessage={t('groupDetails.activity.noPosts')}
      />
    );
  };

  return (
    <section className="group-details" aria-labelledby="group-details-title">
      <header className="create-group__header">
        <button type="button" className="create-group__back" onClick={() => navigate(-1)}>
          <ChevronLeftIcon size={20} />
          {t('groupDetails.title')}
        </button>
      </header>

      <div className="group-details__hero">
        <GroupImage
          src={coverUrl}
          imageClassName="group-details__cover"
          fallbackClassName="group-details__cover group-details__cover--empty"
          fallback={null}
        />
        <div className="group-details__identity">
          <GroupImage
            src={avatarUrl}
            imageClassName="group-details__avatar"
            fallbackClassName="group-details__avatar group-details__avatar--empty"
            fallback={<GroupsTabIcon size={32} />}
          />
          <div className="group-details__meta">
            <h1 id="group-details-title" className="group-details__name">{group.groupName}</h1>
            <p className="group-details__sub">
              <PrivacyIcon size={14} />
              {t(isPrivate ? 'groupDetails.privateGroup' : 'groupDetails.publicGroup')}
              {' · '}
              {t('home.allGroups.members', { count: group.totalMembers.toLocaleString() })}
            </p>
          </div>
          <div className="group-details__actions">
            {!isMember && !isAdmin && renderMembershipAction()}
            <button
              ref={menuAnchorRef}
              type="button"
              className="group-details__kebab"
              onClick={(e) => {
                if (menuOpen) {
                  closeMenu();
                } else {
                  menuAnchorRef.current = e.currentTarget;
                  setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                  setMenuOpen(true);
                }
              }}
              aria-label={t('groupDetails.options.groupOptions')}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
            >
              <PostMenuIcon size={20} />
            </button>
          </div>
        </div>
      </div>

      {canPost && (
        <div className="group-details__compose">
          <Button fullWidth onClick={openCreatePost}>{t('groupDetails.createPost')}</Button>
        </div>
      )}

      <div className="group-tabs group-tabs--standalone">
        <div className="group-tabs__list" role="tablist" aria-label={t('groupDetails.title')}>
          {tabs.map(tab => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                id={`group-detail-tab-${tab}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="group-detail-panel"
                tabIndex={isActive ? 0 : -1}
                className={`group-tabs__tab${isActive ? ' group-tabs__tab--active' : ''}`}
                onClick={() => selectTab(tab)}
              >
                {t(TAB_LABEL_KEYS[tab])}
                {tab === 'userApproval' && pendingRequestCount > 0 && (
                  <span className="group-tabs__count">
                    {pendingRequestCount.toLocaleString()}
                    <span className="sr-only">
                      {' '}
                      {t('groupDetails.userApproval.pendingRequests', { count: pendingRequestCount })}
                    </span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div
        id="group-detail-panel"
        role="tabpanel"
        aria-labelledby={`group-detail-tab-${activeTab}`}
        tabIndex={0}
      >
        {renderTabContent()}
      </div>

      {/* Group kebab dropdown menu — rendered in a portal so it escapes any
          parent overflow/transform/stacking-context clipping. Position is
          viewport-aware: it flips above the trigger when space below is tight,
          and shifts left when the right edge would overflow. */}
      {menuOpen && menuAnchorRect && createPortal(
        <>
          <div className="group-details__menu-backdrop" onClick={closeMenu} />
          <div
            className="group-details__menu"
            role="menu"
            style={anchoredMenuPosition(menuAnchorRect, {
              width: GROUP_MENU_WIDTH,
              height: GROUP_MENU_HEIGHT,
              edge: GROUP_MENU_VIEWPORT_EDGE,
            })}
          >
            {isAdmin && (
              <button type="button" className="group-details__menu-item" role="menuitem" onClick={() => handleMenuAction('editGroup')}>
                <PencilIcon size={16} />
                {t('groupDetails.options.editGroup')}
              </button>
            )}
            <button type="button" className="group-details__menu-item" role="menuitem" onClick={() => handleMenuAction('shareGroup')}>
              <ShareIcon size={16} />
              {t('groupDetails.options.shareGroup')}
            </button>
            <button type="button" className="group-details__menu-item" role="menuitem" onClick={() => handleMenuAction('copyLink')}>
              <LinkIcon size={16} />
              {t('groupDetails.options.copyLink')}
            </button>
            {isAdmin && (
              <button type="button" className="group-details__menu-item group-details__menu-item--danger" role="menuitem" onClick={() => handleMenuAction('deleteGroup')}>
                <TrashIcon size={16} />
                {t('groupDetails.options.deleteGroup')}
              </button>
            )}
            {(isMember || isAdmin) && (
              <button type="button" className="group-details__menu-item group-details__menu-item--danger" role="menuitem" onClick={() => handleMenuAction('leaveGroup')}>
                <LogoutIcon size={16} />
                {t('groupDetails.options.leaveGroup')}
              </button>
            )}
          </div>
        </>,
        document.body,
      )}

      {/* Assign admin before leaving — admin must hand over ownership */}
      {assignAdminOpen && (
        <ConfirmModal
          title={t('groupDetails.options.assignNewAdminBeforeLeaving')}
          message={t('groupDetails.options.assignNewAdminDescription')}
          confirmLabel={t('groupDetails.options.assignAdmin')}
          cancelLabel={t('groupDetails.options.cancel')}
          onClose={() => setAssignAdminOpen(false)}
          onConfirm={() => {
            setAssignAdminOpen(false);
            selectTab('members');
          }}
          showIcon
        />
      )}

      {/* Leave group confirmation (non-admin) */}
      {leaveConfirmOpen && (
        <ConfirmModal
          title={t('groupDetails.options.leaveGroup')}
          message={t('groupDetails.options.leaveGroupConfirm')}
          confirmLabel={t('groupDetails.options.leaveGroup')}
          cancelLabel={t('common.buttons.cancel')}
          onClose={() => setLeaveConfirmOpen(false)}
          onConfirm={handleConfirmLeave}
          loading={membershipBusy}
        />
      )}

      {/* Delete group confirmation */}
      {deleteConfirmOpen && (
        <ConfirmModal
          title={t('groupDetails.options.deleteGroupTitle')}
          message={t('groupDetails.options.deleteGroupDescription')}
          confirmLabel={t('groupDetails.options.deleteGroup')}
          cancelLabel={t('common.buttons.cancel')}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            if (!group) return;
            setDeleteConfirmOpen(false);
            setMembershipBusy(true);
            deleteGroup(group.id)
              .then(() => navigate('/home'))
              .catch((err: unknown) => {
                ToastService.show({
                  type: 'error',
                  message: getApiErrorMessage(err as ApiError) || t('common.errors.generic'),
                });
              })
              .finally(() => setMembershipBusy(false));
          }}
          loading={membershipBusy}
          showIcon
        />
      )}
    </section>
  );
}
